import { DependencyList, useEffect, useState } from 'react';
import { hasCoachMarkRectChanged } from '../utils/coachMarkMeasurement';

const COACH_MARK_TARGET_SETTLE_MS = 520;
const COACH_MARK_OBSERVED_PARENT_LIMIT = 5;

interface DynamicCoachMarkTargetRectOptions {
  enabled: boolean;
  getTarget: () => HTMLElement | null;
  refreshDeps?: DependencyList;
  settleMs?: number;
}

const collectObservedElements = (target: HTMLElement | null): HTMLElement[] => {
  const elements: HTMLElement[] = [];
  let current = target;
  while (current && elements.length < COACH_MARK_OBSERVED_PARENT_LIMIT) {
    elements.push(current);
    current = current.parentElement;
  }
  return elements;
};

export const useDynamicCoachMarkTargetRect = ({
  enabled,
  getTarget,
  refreshDeps = [],
  settleMs = COACH_MARK_TARGET_SETTLE_MS,
}: DynamicCoachMarkTargetRectOptions): DOMRect | null => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setTargetRect(null);
      return undefined;
    }

    let disposed = false;
    let frameId: number | null = null;
    let settleUntil = window.performance.now() + settleMs;
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => restartMeasurement())
      : null;

    const measureTarget = () => {
      const target = getTarget();
      if (!target) return;
      const nextRect = target.getBoundingClientRect();
      setTargetRect(previous => (
        hasCoachMarkRectChanged(previous, nextRect) ? nextRect : previous
      ));
    };

    const measureLoop = (now: number) => {
      if (disposed) return;
      measureTarget();
      if (now < settleUntil) {
        frameId = window.requestAnimationFrame(measureLoop);
      } else {
        frameId = null;
      }
    };

    function restartMeasurement() {
      if (disposed) return;
      settleUntil = window.performance.now() + settleMs;
      measureTarget();
      if (frameId === null) {
        frameId = window.requestAnimationFrame(measureLoop);
      }
    }

    collectObservedElements(getTarget()).forEach(element => {
      resizeObserver?.observe(element);
    });
    restartMeasurement();
    window.addEventListener('resize', restartMeasurement);
    window.addEventListener('scroll', restartMeasurement, true);
    document.addEventListener('transitionend', restartMeasurement, true);

    return () => {
      disposed = true;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', restartMeasurement);
      window.removeEventListener('scroll', restartMeasurement, true);
      document.removeEventListener('transitionend', restartMeasurement, true);
    };
  }, [enabled, getTarget, settleMs, ...refreshDeps]);

  return targetRect;
};
